import { makeAutoObservable } from 'mobx';
import { token } from '@/infrastructure/container';
import { type Book, type Position, type SearchMatch, type Voice } from '@/domain/book';
import {
  chapterProgressPercent,
  clampPosition,
  nextParagraph,
  skipBackward,
  skipForward,
} from '@/domain/playback';
import { searchBook } from '@/domain/search';
import { baseLang } from '@/services/i18n/messages';
import { type IAnalytics } from '@/services/Analytics';
import { type IAppBus, type AppBusEvents } from '@/services/AppBus';
import { type II18n } from '@/services/i18n/I18n';
import { type ILocalData } from '@/services/LocalData';
import { type ILocalStorage } from '@/services/LocalStorage';
import { type ILogger } from '@/services/Logger';
import {
  type ITTSService,
  type TTSPhase,
  type TTSProvider,
  type TTSProviderId,
  type TTSStatus,
} from '@/services/TTSService';

export const SPEED_OPTIONS = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2] as const;

const BOOK_STORE = 'book';
const PROGRESS_STORE = 'progress';
const CURRENT_KEY = 'current';
// These three keep the pre-rename 'bookbeats.' prefix on purpose: they are localStorage keys, and
// renaming them silently resets every existing user's speed, voice and engine choice.
const SPEED_PREF = 'bookbeats.speed';
const VOICE_PREF = 'bookbeats.voice';
const PROVIDER_PREF = 'bookbeats.ttsProvider';
const DEFAULT_SPEED = 1;

const ORIGIN: Position = { chapter: 0, paragraph: 0 };

// How many chunks to keep queued in the synth (currently speaking + buffered ahead). >1 is what
// removes the gap: the synth has the next paragraph ready before the current finishes.
const LOOKAHEAD = 2;

function chunkId(generation: number, pos: Position): string {
  return `${generation}:${pos.chapter}:${pos.paragraph}`;
}

function parseChunkId(id: string): { generation: number; position: Position } | null {
  const parts = id.split(':').map(Number);
  if (parts.length !== 3 || parts.some(Number.isNaN)) return null;
  return { generation: parts[0], position: { chapter: parts[1], paragraph: parts[2] } };
}

/**
 * The playback engine and the single source of truth for "what are we listening to, where are we".
 * Speaks the book paragraph by paragraph through the injected TTS service, persists position to
 * IndexedDB, and owns the chapter-list / search panel state. All DOM and storage access is behind
 * injected services so the whole thing is testable by `new PlayerStore(...fakes)`.
 */
export class PlayerStore {
  private _book: Book | null = null;
  private _pos: Position = ORIGIN;
  private _isPlaying = false;
  private _isHydrating = true;

  private _speed: number = DEFAULT_SPEED;
  private _voiceId: string | undefined;
  private _voices: Voice[] = [];

  // Which TTS engine is active, plus the load state of its model (only Kokoro has one to download).
  private _ttsProvider: TTSProviderId = 'system';
  // The engine a switch is loading toward, so the selector reflects the click during the download
  // and reverts if it fails. Null when not switching.
  private _switchingTo: TTSProviderId | null = null;
  private _voiceModelPhase: TTSPhase = 'idle';
  private _voiceModelProgress = 0;

  private _isChapterListOpen = false;
  private _isSearchOpen = false;
  private _searchQuery = '';
  private _searchResults: SearchMatch[] = [];

  // Bumped whenever playback intent changes (play, pause, seek, speed/voice). Each queued chunk
  // carries the generation it was queued under; start/end/error callbacks for a stale generation
  // (e.g. an utterance cancelled by a seek) are ignored.
  private _generation = 0;
  // The furthest position we've queued into the synth, and how many chunks are in flight (playing +
  // buffered ahead). We keep LOOKAHEAD chunks queued so the synth plays them back-to-back, gaplessly.
  private _queuedPos: Position | null = null;
  private _inFlight = 0;

  // Toast about a failed save at most once per session, so a broken store never spams the user.
  private _progressSaveWarned = false;

  constructor(
    private bus: IAppBus,
    private tts: ITTSService,
    private localData: ILocalData,
    private localStorage: ILocalStorage,
    private analytics: IAnalytics,
    private i18n: II18n,
    private logger: ILogger,
  ) {
    makeAutoObservable(this);
    this._speed = this.readSpeedPref();
    this._ttsProvider = this.readProviderPref();
    this._voiceId = this.localStorage.get(this.voicePrefKey(this._ttsProvider)) ?? undefined;
    bus.subscribe('BOOK_LOADED', this.onBookLoaded);
    bus.subscribe('BOOK_CLEARED', this.onBookCleared);
    bus.subscribe('KEY_DOWN', this.onKeyDown);
    this.tts.onStatus(this.onTtsStatus);
    this.tts.setHandlers({
      onStart: this.onChunkStart,
      onEnd: this.onChunkEnd,
      onError: this.onChunkError,
    });
    void this.hydrate();
  }

  // ---- read surface -------------------------------------------------------

  get isHydrating(): boolean {
    return this._isHydrating;
  }

  get hasBook(): boolean {
    return this._book !== null;
  }

  get title(): string {
    return this._book?.title ?? '';
  }

  get chapters(): readonly { label: string }[] {
    return this._book?.chapters ?? [];
  }

  get position(): Position {
    return this._pos;
  }

  get isPlaying(): boolean {
    return this._isPlaying;
  }

  get currentChapterLabel(): string {
    return this._book?.chapters[this._pos.chapter]?.label ?? '';
  }

  get currentParagraphText(): string {
    return this._book?.chapters[this._pos.chapter]?.paragraphs[this._pos.paragraph] ?? '';
  }

  get chapterProgressPercent(): number {
    if (!this._book) return 0;
    return chapterProgressPercent(this._book.chapters, this._pos);
  }

  get speed(): number {
    return this._speed;
  }

  get speedOptions(): readonly number[] {
    return SPEED_OPTIONS;
  }

  get voices(): readonly Voice[] {
    return this._voices;
  }

  get voiceId(): string | undefined {
    return this._voiceId;
  }

  get ttsProviders(): readonly TTSProvider[] {
    return this.tts.providers();
  }

  get ttsProvider(): TTSProviderId {
    return this._switchingTo ?? this._ttsProvider;
  }

  /** The neural voice model is downloading/initializing. Play is gated and the voice list is empty
      until it resolves. */
  get isVoiceModelLoading(): boolean {
    return this._voiceModelPhase === 'loading';
  }

  /** 0..1 download/init progress, meaningful while {@link isVoiceModelLoading}. */
  get voiceModelProgress(): number {
    return this._voiceModelProgress;
  }

  get voiceModelFailed(): boolean {
    return this._voiceModelPhase === 'error';
  }

  get isChapterListOpen(): boolean {
    return this._isChapterListOpen;
  }

  get isSearchOpen(): boolean {
    return this._isSearchOpen;
  }

  get searchQuery(): string {
    return this._searchQuery;
  }

  get searchResults(): readonly SearchMatch[] {
    return this._searchResults;
  }

  // ---- playback -----------------------------------------------------------

  togglePlay = (): void => {
    if (this._isPlaying) this.pause();
    else this.play();
  };

  play = (): void => {
    if (!this._book || this._isPlaying || this.currentParagraphText === '') return;
    // The neural model isn't ready yet: don't enqueue into an engine that would only error.
    if (this.isVoiceModelLoading) return;
    this._isPlaying = true;
    this.analytics.listenStarted();
    this.startQueue();
  };

  pause = (): void => {
    const wasPlaying = this._isPlaying;
    this._isPlaying = false;
    this.resetQueue();
    this.tts.stop();
    if (wasPlaying) this.analytics.listenStopped(this._book?.format);
  };

  skipForward = (): void => {
    if (!this._book) return;
    this.seek(skipForward(this._book.chapters, this._pos, this._speed));
  };

  skipBackward = (): void => {
    if (!this._book) return;
    this.seek(skipBackward(this._book.chapters, this._pos, this._speed));
  };

  goToChapter = (chapterIndex: number): void => {
    if (!this._book) return;
    this.seek({ chapter: chapterIndex, paragraph: 0 });
    this.closeChapterList();
  };

  nextChapter = (): void => {
    if (this._book) this.goToChapter(this._pos.chapter + 1);
  };

  previousChapter = (): void => {
    if (this._book) this.goToChapter(this._pos.chapter - 1);
  };

  jumpTo = (match: Position): void => {
    this.seek({ chapter: match.chapter, paragraph: match.paragraph });
    this.closeSearch();
  };

  private seek = (pos: Position): void => {
    if (!this._book) return;
    this._pos = clampPosition(this._book.chapters, pos);
    void this.persistProgress();
    if (this._isPlaying) {
      // Drop the buffered chunks and re-queue from the new position under a fresh generation.
      this.tts.stop();
      this.startQueue();
    }
  };

  /** Begin a fresh playback generation at the current position and fill the look-ahead buffer. */
  private startQueue = (): void => {
    this.resetQueue();
    this.fillQueue(this._generation);
  };

  /** Invalidate any in-flight chunks (their callbacks become stale) and clear the buffer. */
  private resetQueue = (): void => {
    this._generation++;
    this._queuedPos = null;
    this._inFlight = 0;
  };

  /** Queue chunks until the look-ahead buffer is full (or the book ends), under `generation`. */
  private fillQueue = (generation: number): void => {
    if (!this._book) return;
    while (this._inFlight < LOOKAHEAD && this._generation === generation) {
      const next = this.nextToQueue();
      if (!next) break;
      this._queuedPos = next;
      this._inFlight++;
      this.tts.enqueue(
        { id: chunkId(generation, next), text: this.textAt(next) },
        { rate: this._speed, voiceId: this._voiceId },
      );
    }
  };

  /** The next position to queue: the current one when priming, else the paragraph after the last
      queued. Skips empty paragraphs (which the synth would silently swallow). */
  private nextToQueue = (): Position | null => {
    if (!this._book) return null;
    let candidate: Position | null =
      this._queuedPos === null ? this._pos : nextParagraph(this._book.chapters, this._queuedPos);
    while (candidate && this.textAt(candidate).trim() === '') {
      candidate = nextParagraph(this._book.chapters, candidate);
    }
    return candidate;
  };

  private textAt = (pos: Position): string =>
    this._book?.chapters[pos.chapter]?.paragraphs[pos.paragraph] ?? '';

  private onChunkStart = (id: string): void => {
    const parsed = parseChunkId(id);
    if (!parsed || parsed.generation !== this._generation) return;
    this._pos = parsed.position;
    void this.persistProgress();
  };

  private onChunkEnd = (id: string): void => {
    const parsed = parseChunkId(id);
    if (!parsed || parsed.generation !== this._generation) return;
    this._inFlight--;
    this.fillQueue(this._generation);
    if (this._inFlight <= 0) {
      this._isPlaying = false; // nothing left queued: the book finished
      this.analytics.listenStopped(this._book?.format);
    }
  };

  private onChunkError = (id: string, error: unknown): void => {
    const parsed = parseChunkId(id);
    if (!parsed || parsed.generation !== this._generation) return;
    this.logger.error('PlayerStore: TTS error', error);
    this._isPlaying = false;
    this.resetQueue();
    this.tts.stop();
    this.bus.toast('error', this.i18n.t('toast.playbackError'));
    this.analytics.listenStopped(this._book?.format);
    this.analytics.error(`tts: ${String(error)}`);
  };

  // ---- preferences (global, localStorage) ---------------------------------

  setSpeed = (speed: number): void => {
    if (!SPEED_OPTIONS.includes(speed as (typeof SPEED_OPTIONS)[number])) return;
    this._speed = speed;
    this.localStorage.set(SPEED_PREF, String(speed));
    this.restartIfPlaying();
  };

  setVoice = (voiceId: string): void => {
    this._voiceId = voiceId;
    this.localStorage.set(this.voicePrefKey(this._ttsProvider), voiceId);
    this.restartIfPlaying();
  };

  // Switch TTS engine. Kokoro triggers a one-time model download; the UI shows progress via
  // `isVoiceModelLoading` / `voiceModelProgress` while `setProvider` resolves.
  setTtsProvider = (id: TTSProviderId): void => {
    if (id === this._ttsProvider) return;
    this.pause();
    void this.switchProvider(id);
  };

  private switchProvider = async (id: TTSProviderId): Promise<void> => {
    this._switchingTo = id;
    try {
      await this.tts.setProvider(id);
      this._ttsProvider = id;
      this.localStorage.set(PROVIDER_PREF, id);
      this._voices = this.tts.voices();
      // Restore this provider's own remembered voice, then fit it to the current book's language.
      this._voiceId = this.localStorage.get(this.voicePrefKey(id)) ?? undefined;
      this.reconcileVoice();
    } catch (error) {
      // The engine stays on the previous provider (the router does not switch on a failed load).
      this.logger.error('PlayerStore.switchProvider', error);
      this.bus.toast('error', this.i18n.t('voice.modelError'));
    } finally {
      this._switchingTo = null;
    }
  };

  // Fires from the router as a provider loads (worker download progress, then ready/error). An
  // arrow method, so makeAutoObservable wraps it as an action and these mutations stay tracked.
  private onTtsStatus = (status: TTSStatus): void => {
    this._voiceModelPhase = status.phase;
    this._voiceModelProgress = status.progress;
  };

  private voicePrefKey = (provider: TTSProviderId): string =>
    // System keeps the original key so existing users' saved voice survives; Kokoro is namespaced,
    // since its voice ids are disjoint from the platform's.
    provider === 'system' ? VOICE_PREF : `${VOICE_PREF}.${provider}`;

  private readProviderPref = (): TTSProviderId => {
    return this.localStorage.get(PROVIDER_PREF) === 'kokoro' ? 'kokoro' : 'system';
  };

  private restartIfPlaying = (): void => {
    // Web Speech can't retune a live utterance; re-speak the current paragraph with the new setting.
    if (this._isPlaying) this.seek(this._pos);
  };

  private readSpeedPref = (): number => {
    const raw = this.localStorage.get(SPEED_PREF);
    const parsed = raw === null ? NaN : Number(raw);
    return SPEED_OPTIONS.includes(parsed as (typeof SPEED_OPTIONS)[number]) ? parsed : DEFAULT_SPEED;
  };

  // ---- panels -------------------------------------------------------------

  toggleChapterList = (): void => {
    this._isChapterListOpen = !this._isChapterListOpen;
  };

  closeChapterList = (): void => {
    this._isChapterListOpen = false;
  };

  toggleSearch = (): void => {
    this._isSearchOpen = !this._isSearchOpen;
  };

  closeSearch = (): void => {
    this._isSearchOpen = false;
  };

  setSearchQuery = (query: string): void => {
    this._searchQuery = query;
    this._searchResults = this._book ? searchBook(this._book.chapters, query) : [];
  };

  // ---- persistence / lifecycle -------------------------------------------

  private hydrate = async (): Promise<void> => {
    try {
      await this.tts.initialize();
      // Re-activate a previously chosen Kokoro voice (cached after the first download). If it fails
      // to load now (e.g. offline), quietly fall back to the always-available system voices.
      if (this._ttsProvider === 'kokoro') {
        try {
          await this.tts.setProvider('kokoro');
        } catch (error) {
          this.logger.error('PlayerStore.hydrate: Kokoro load failed, falling back to system', error);
          this._ttsProvider = 'system';
          this._voiceId = this.localStorage.get(this.voicePrefKey('system')) ?? undefined;
        }
      }
      this._voices = this.tts.voices();
      const book = await this.localData.get<Book>(BOOK_STORE, CURRENT_KEY);
      if (book) {
        this._book = book;
        const pos = await this.localData.get<Position>(PROGRESS_STORE, CURRENT_KEY);
        this._pos = pos ? clampPosition(book.chapters, pos) : ORIGIN;
      }
      this.reconcileVoice(); // after the book is known, so we can match its language
    } catch (error) {
      this.logger.error('PlayerStore.hydrate', error);
    } finally {
      this._isHydrating = false;
    }
  };

  private onBookLoaded = async (): Promise<void> => {
    this.pause();
    const book = await this.localData.get<Book>(BOOK_STORE, CURRENT_KEY);
    if (!book) return;
    this._book = book;
    this._pos = ORIGIN;
    this.reconcileVoice(); // a French book should read in a French voice
    this.closeSearch();
    this.closeChapterList();
    this.setSearchQuery('');
    await this.persistProgress();
  };

  private onBookCleared = (): void => {
    this.pause();
    this._book = null;
    this._pos = ORIGIN;
    this.setSearchQuery('');
    this.closeChapterList();
    this.closeSearch();
  };

  private onKeyDown = (data: AppBusEvents['KEY_DOWN']): void => {
    if (!this._book || data.ctrl || data.meta || data.alt) return;
    if (data.key === 'Escape') {
      this.closeChapterList();
      this.closeSearch();
    } else if (data.key === ' ') {
      data.preventDefault();
      this.togglePlay();
    } else if (data.key === 'ArrowRight') {
      this.skipForward();
    } else if (data.key === 'ArrowLeft') {
      this.skipBackward();
    }
  };

  // Choose a voice that matches the book's language (falling back to the UI language). Keeps the
  // saved voice if it already fits, so a manual choice survives across books of the same language.
  private reconcileVoice = (): void => {
    // Never overwrite a saved preference when the platform hasn't reported any voices yet.
    if (this._voices.length === 0) return;
    const target = baseLang(this._book?.language ?? this.i18n.locale);
    const current = this._voices.find((v) => v.id === this._voiceId);
    if (current && baseLang(current.lang) === target) return;
    const match = this._voices.find((v) => baseLang(v.lang) === target);
    this._voiceId = (match ?? current ?? this._voices[0])?.id;
  };

  private persistProgress = async (): Promise<void> => {
    // A plain snapshot: this._pos is a MobX observable proxy, which IndexedDB cannot structured-clone.
    const snapshot: Position = { chapter: this._pos.chapter, paragraph: this._pos.paragraph };
    try {
      await this.localData.put<Position>(PROGRESS_STORE, CURRENT_KEY, snapshot);
    } catch (error) {
      // Saving the place is best-effort; a storage failure must never stop the audio.
      this.logger.warn('PlayerStore: failed to save progress', error);
      if (!this._progressSaveWarned) {
        this._progressSaveWarned = true;
        this.bus.toast('error', this.i18n.t('toast.saveFailed'));
      }
    }
  };
}

export const TOKEN_PlayerStore = token<PlayerStore>('PlayerStore');
